import { createApi } from "@reduxjs/toolkit/query/react";
import { listAlertsRequest, listAlertsResponse } from "../../types";
import { updateAlertStatusRequest } from "../../types/api/request/alert";
import { axiosBaseQuery } from "../../providers/auth0-instance";
import { withErrorNotification, notifyQueryError } from "../notifications";
import { alertCountsApi } from "../alertsCountsInQuery";

export const alertApi = createApi({
  reducerPath: "alertApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Alert"],
  endpoints: (builder) => ({
    listAlerts: builder.query<
      listAlertsResponse,
      listAlertsRequest & { org?: string }
    >({
      // org is pulled out rather than spread: it belongs in the header, and
      // leaving it in `params` would send it as a query string the API ignores.
      query: ({ org, ...params }) => ({
        url: "/alerts-with-filters",
        method: "GET",
        params,
        org,
      }),
      providesTags: [{ type: "Alert", id: "LIST" }],
      onQueryStarted: withErrorNotification("Failed to fetch alerts"),
    }),
    updateAlertStatus: builder.mutation<void, updateAlertStatusRequest>({
      query: ({ id, status }) => ({
        url: `/alert/${id}`,
        method: "PUT",
        data: { status },
      }),
      onQueryStarted: async (
        { id, status },
        { dispatch, queryFulfilled, getState },
      ) => {
        const patches = alertApi.util
          .selectInvalidatedBy(getState(), [{ type: "Alert", id: "LIST" }])
          .map(({ originalArgs }) =>
            dispatch(
              alertApi.util.updateQueryData(
                "listAlerts",
                originalArgs,
                (draft) => {
                  const item = draft.items.find((a) => a._id === id);
                  if (item) item.status = status;
                },
              ),
            ),
          );
        try {
          await queryFulfilled;
          dispatch(alertCountsApi.util.invalidateTags(["AlertCounts"]));
        } catch (err) {
          patches.forEach((p) => p.undo());
          notifyQueryError(dispatch, "Failed to update alert status", err, {
            alwaysNotify: true,
          });
        }
      },
    }),
  }),
});

export const { useListAlertsQuery, useUpdateAlertStatusMutation } = alertApi;
